import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import {
  DEFAULT_HARBOUR_FILTERS,
  HARBOUR_DEFAULT_HAS_PHONE,
  HARBOUR_DEFAULT_HIDE_PARKED,
  HARBOUR_DEFAULT_OWNER_FILTER,
  HARBOUR_DEFAULT_PLAN_FILTER,
  HARBOUR_OWNER_FILTER,
  HARBOUR_TIMEZONE,
  isHarbourOwnerFilter,
  isHarbourPlanFilter,
} from 'src/constants/harbour'

dayjs.extend(utc)
dayjs.extend(timezone)

/**
 * Sticky Harbour filters. Search is never persisted.
 *
 * Hide parked and Has phone last until you change them (Hide parked on,
 * Has phone off). Owner and plan last this UK day only — yesterday’s
 * Paid + All must not greet you tomorrow.
 *
 * A `?venue=` Slack link lifts Unassigned → All so My work is not hidden
 * under the drawer. That lift is saved for the rest of the UK day.
 *
 * Storage key is per account so shared browsers don’t mix queues.
 */
export const HARBOUR_FILTER_PREFS_KEY_PREFIX = 'harbour.filterPrefs'

const getStorage = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null
  }
  return window.localStorage
}

/**
 * @param {string|null|undefined} accountId
 * @returns {string|null}
 */
export function buildHarbourFilterPrefsStorageKey(accountId) {
  const id = String(accountId || '').trim()
  if (!id) {
    return null
  }
  return `${HARBOUR_FILTER_PREFS_KEY_PREFIX}.${id}`
}

/**
 * London calendar day for owner / plan chips.
 * @param {Date|string|number} [now]
 */
export function getHarbourFilterPrefsDayKey(now = new Date()) {
  return dayjs.tz(now, HARBOUR_TIMEZONE).format('YYYY-MM-DD')
}

/**
 * Normalize sticky fields only (drops search, savedOn, and unknown keys).
 * Invalid enums fall back to board defaults. Hide parked defaults true.
 * Has phone defaults false so Search/Needs you still show dead numbers
 * until they opt in.
 * @param {object} [raw]
 */
export function normalizeHarbourFilterPrefs(raw = {}) {
  const ownerFilter = isHarbourOwnerFilter(raw?.ownerFilter)
    ? raw.ownerFilter
    : HARBOUR_DEFAULT_OWNER_FILTER
  const planFilter = isHarbourPlanFilter(raw?.planFilter)
    ? raw.planFilter
    : HARBOUR_DEFAULT_PLAN_FILTER
  const hideParked =
    raw?.hideParked == null
      ? HARBOUR_DEFAULT_HIDE_PARKED
      : Boolean(raw.hideParked)
  const hasPhone =
    raw?.hasPhone == null ? HARBOUR_DEFAULT_HAS_PHONE : Boolean(raw.hasPhone)

  return {
    ownerFilter,
    planFilter,
    hideParked,
    hasPhone,
  }
}

/**
 * Owner / plan roll back to Mine + All after the UK day (or a blob with
 * no savedOn — leftover Paid + All from testing). Hide parked and Has
 * phone stay.
 * @param {object} [raw]
 * @param {Date|string|number} [now]
 */
export function reconcileHarbourFilterPrefs(raw = {}, now = new Date()) {
  const prefs = normalizeHarbourFilterPrefs(raw)
  const savedOn = getHarbourFilterPrefsDayKey(now)
  if (String(raw?.savedOn || '').trim() === savedOn) {
    return { prefs, savedOn, rolled: false }
  }
  return {
    prefs: {
      ownerFilter: HARBOUR_DEFAULT_OWNER_FILTER,
      planFilter: HARBOUR_DEFAULT_PLAN_FILTER,
      hideParked: prefs.hideParked,
      hasPhone: prefs.hasPhone,
    },
    savedOn,
    rolled: true,
  }
}

function persistHarbourFilterPrefs(key, prefs, savedOn, storage) {
  storage.setItem(key, JSON.stringify({ ...prefs, savedOn }))
}

export function loadHarbourFilterPrefs(
  accountId,
  storage = getStorage(),
  now = new Date(),
) {
  const key = buildHarbourFilterPrefsStorageKey(accountId)
  if (!key || !storage) {
    return null
  }

  try {
    const raw = storage.getItem(key)
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null
    }
    const { prefs, savedOn, rolled } = reconcileHarbourFilterPrefs(parsed, now)
    if (rolled) {
      try {
        persistHarbourFilterPrefs(key, prefs, savedOn, storage)
      } catch {
        // Still return the cleaned view if localStorage is full / blocked.
      }
    }
    return prefs
  } catch {
    return null
  }
}

export function saveHarbourFilterPrefs(
  accountId,
  filters = {},
  storage = getStorage(),
  now = new Date(),
) {
  const key = buildHarbourFilterPrefsStorageKey(accountId)
  if (!key || !storage) {
    return false
  }

  const prefs = normalizeHarbourFilterPrefs(filters)

  try {
    persistHarbourFilterPrefs(
      key,
      prefs,
      getHarbourFilterPrefsDayKey(now),
      storage,
    )
    return true
  } catch {
    return false
  }
}

/**
 * Merge sticky prefs onto board filter defaults. Search always starts empty.
 */
export function mergeHarbourFilterPrefs(defaults = {}, prefs = null) {
  const base = {
    ownerFilter: defaults.ownerFilter || HARBOUR_DEFAULT_OWNER_FILTER,
    planFilter: defaults.planFilter || HARBOUR_DEFAULT_PLAN_FILTER,
    hideParked:
      defaults.hideParked == null
        ? HARBOUR_DEFAULT_HIDE_PARKED
        : Boolean(defaults.hideParked),
    hasPhone:
      defaults.hasPhone == null
        ? HARBOUR_DEFAULT_HAS_PHONE
        : Boolean(defaults.hasPhone),
    search: '',
  }

  if (!prefs) {
    return {
      ...base,
      search: String(defaults.search || ''),
    }
  }

  return {
    ...base,
    ...normalizeHarbourFilterPrefs(prefs),
    search: '',
  }
}

/**
 * Slack `?venue=` must land on a view that can show claimed work.
 * Unassigned hides My work; All still shows Needs you plus everyone’s
 * open cases. My work and All are left as-is. Plan is unchanged.
 *
 * @param {object} [filters]
 * @param {string|null|undefined} venueId
 */
export function applyHarbourDeepLinkOwnerView(filters = {}, venueId) {
  if (!venueId || filters.ownerFilter !== HARBOUR_OWNER_FILTER.UNASSIGNED) {
    return filters
  }
  return { ...filters, ownerFilter: HARBOUR_OWNER_FILTER.ALL }
}

export { DEFAULT_HARBOUR_FILTERS }
